// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

// Mobile reports surface (Capacitor UI). Summary-first, drill-through screens
// that reuse the DESKTOP reports' computation logic (BalanceSheet, IncomeStatement,
// TransactionReport) rather than their table JSX. The three recharts reports are
// already responsive, so we render them directly inside thin mobile wrappers.

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../store/AppContext.js';
import {
  formatAmount,
  ASSET_TYPES,
  LIABILITY_TYPES,
  INCOME_TYPES,
  EXPENSE_TYPES,
} from '../../lib/eventLog.js';
import { getExchangeRate, convertCurrency } from '../../lib/currency-math.js';
import { getCurrencyDecimalPlaces } from '../../lib/currencies.js';
import { formatDate } from '../../lib/formatLocale.js';
import { MobileTopBar } from './MobileTopBar.js';
import { BottomSheet } from './BottomSheet.js';
import { ChevronRightIcon, ChevronDownIcon, SearchIcon } from './icons.js';
import { NetWorthChart } from '../reports/NetWorthChart.js';
import { ExpenseBreakdown } from '../reports/ExpenseBreakdown.js';
import { IncomeVsExpenses } from '../reports/IncomeVsExpenses.js';
import type { Account } from '../../types.js';

// ─── Shared scroll wrapper ──────────────────────────────────────────────────

// Every full report screen shares this shell: a fixed top bar (laid out by the
// parent flex column) followed by a single scroll region that clears the tab bar.
function Screen({
  title,
  back = true,
  right,
  children,
}: {
  title: string;
  back?: boolean;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <>
      <MobileTopBar title={title} back={back} right={right} />
      <div className="flex-1 overflow-y-auto native-scroll pb-tabbar">{children}</div>
    </>
  );
}

// A tappable disclosure header for the collapsible statement sections.
function SectionHeader({
  label,
  open,
  onToggle,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      className="w-full touch-row flex items-center gap-2 px-4 h-12 border-b border-border bg-bg-surface active:bg-bg-elevated text-left"
      aria-expanded={open}
    >
      <span className="text-text-muted shrink-0">
        {open ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
      </span>
      <span className="text-xs font-medium text-text-subtle uppercase tracking-widest">{label}</span>
    </button>
  );
}

// An account row that drills into that account's register.
function AccountRow({
  name,
  currency,
  amount,
  colorClass,
  onOpen,
}: {
  name: string;
  currency: string;
  amount: number;
  colorClass: string;
  onOpen: () => void;
}) {
  return (
    <button
      onClick={onOpen}
      className="w-full touch-row flex items-center justify-between gap-3 px-4 h-12 border-b border-border text-left active:bg-bg-elevated"
    >
      <span className="flex items-center gap-2 min-w-0">
        <span className="text-md text-text truncate">{name}</span>
      </span>
      <span className="flex items-center gap-1.5 shrink-0">
        <span className={`text-sm font-mono tabular-nums ${colorClass}`}>
          {formatAmount(amount, currency)}
        </span>
        <span className="text-text-subtle">
          <ChevronRightIcon size={16} />
        </span>
      </span>
    </button>
  );
}

// ─── Reports hub ────────────────────────────────────────────────────────────

const HUB_GROUPS = [
  {
    headingKey: 'titles.statements',
    links: [
      { labelKey: 'titles.balanceSheet', route: '/reports/balance-sheet' },
      { labelKey: 'titles.incomeStatement', route: '/reports/income-statement' },
      { labelKey: 'titles.cashFlow', route: '/reports/cash-flow' },
      { labelKey: 'hub.budget', route: '/reports/budget' },
    ],
  },
  {
    headingKey: 'hub.charts',
    links: [
      { labelKey: 'titles.netWorth', route: '/reports/net-worth' },
      { labelKey: 'titles.expenseBreakdown', route: '/reports/expense-breakdown' },
      { labelKey: 'titles.incomeVsExpenses', route: '/reports/income-vs-expenses' },
    ],
  },
  {
    headingKey: 'hub.detail',
    links: [
      { labelKey: 'titles.transactionReport', route: '/reports/transactions' },
      { labelKey: 'hub.capitalGains', route: '/reports/capital-gains' },
    ],
  },
] as const;

export function MobileReportsHub() {
  const { t } = useTranslation(['reports']);
  const navigate = useNavigate();
  return (
    <Screen title={t('titles.reports')} back={false}>
      {HUB_GROUPS.map((group) => (
        <section key={group.headingKey}>
          <h2 className="px-4 pt-4 pb-1 text-xs font-medium text-text-subtle uppercase tracking-widest">
            {t(group.headingKey)}
          </h2>
          {group.links.map((link) => (
            <button
              key={link.route}
              onClick={() => navigate(link.route)}
              className="w-full touch-row flex items-center justify-between gap-3 px-4 h-12 border-b border-border text-left active:bg-bg-elevated"
            >
              <span className="text-md text-text">{t(link.labelKey)}</span>
              <span className="text-text-subtle">
                <ChevronRightIcon size={18} />
              </span>
            </button>
          ))}
        </section>
      ))}
    </Screen>
  );
}

// ─── Balance sheet ──────────────────────────────────────────────────────────

// Mirrors BalanceSheet.tsx: each non-placeholder account is converted to the
// home currency (transaction-free, "as of today"), and totals are the sum of
// those converted amounts so the totals always reconcile with the listed rows.
export function MobileBalanceSheet() {
  const { t } = useTranslation(['reports', 'common']);
  const { state } = useApp();
  const { ledger } = state;
  const navigate = useNavigate();
  const homeCurrency = ledger.settings.homeCurrency;
  const today = new Date().toISOString().slice(0, 10);

  const [assetsOpen, setAssetsOpen] = useState(true);
  const [liabilitiesOpen, setLiabilitiesOpen] = useState(true);

  const allPrices = useMemo(() => [...ledger.prices.values()], [ledger.prices]);

  const { assets, liabilities, totalAssets, totalLiabilities } = useMemo(() => {
    function toHome(account: Account): number {
      if (account.currency === homeCurrency) return account.balance;
      const rate = getExchangeRate(account.currency, homeCurrency, today, allPrices);
      if (rate === null) return 0;
      return convertCurrency(
        account.balance,
        account.currency,
        homeCurrency,
        rate,
        getCurrencyDecimalPlaces(account.currency),
        getCurrencyDecimalPlaces(homeCurrency),
      );
    }

    const all = [...ledger.accounts.values()];
    const assetAccounts = all
      .filter((a) => ASSET_TYPES.has(a.type) && !a.placeholder)
      .map((a) => ({ account: a, home: toHome(a) }))
      .sort((a, b) => a.account.name.localeCompare(b.account.name));
    const liabilityAccounts = all
      .filter((a) => LIABILITY_TYPES.has(a.type) && !a.placeholder)
      .map((a) => ({ account: a, home: toHome(a) }))
      .sort((a, b) => a.account.name.localeCompare(b.account.name));

    return {
      assets: assetAccounts,
      liabilities: liabilityAccounts,
      totalAssets: assetAccounts.reduce((sum, r) => sum + r.home, 0),
      totalLiabilities: liabilityAccounts.reduce((sum, r) => sum + r.home, 0),
    };
  }, [ledger.accounts, homeCurrency, today, allPrices]);

  const netWorth = totalAssets + totalLiabilities;

  return (
    <Screen title={t('titles.balanceSheet')}>
      <div className="px-4 py-4 border-b border-border bg-bg-surface">
        <p className="text-xs text-text-subtle mb-2">
          {t('controls.asOf', { date: formatDate(new Date(), { month: 'long', day: 'numeric', year: 'numeric' }) })}
        </p>
        <SummaryRow label={t('sections.assets')} amount={totalAssets} currency={homeCurrency} />
        <SummaryRow label={t('sections.liabilities')} amount={totalLiabilities} currency={homeCurrency} />
        <div className="mt-1 pt-2 border-t border-border">
          <SummaryRow
            label={t('sections.netWorth')}
            amount={netWorth}
            currency={homeCurrency}
            emphasized
            colorClass={netWorth >= 0 ? 'text-positive' : 'text-negative'}
          />
        </div>
      </div>

      <SectionHeader label={t('sections.assets')} open={assetsOpen} onToggle={() => setAssetsOpen((x) => !x)} />
      {assetsOpen &&
        (assets.length === 0 ? (
          <p className="px-4 py-3 text-sm text-text-subtle border-b border-border">{t('common:emptyState.none')}</p>
        ) : (
          assets.map(({ account, home }) => (
            <AccountRow
              key={account.id}
              name={account.name}
              currency={homeCurrency}
              amount={home}
              colorClass={home < 0 ? 'text-negative' : 'text-text'}
              onOpen={() => navigate(`/accounts/${account.id}`)}
            />
          ))
        ))}

      <SectionHeader
        label={t('sections.liabilities')}
        open={liabilitiesOpen}
        onToggle={() => setLiabilitiesOpen((x) => !x)}
      />
      {liabilitiesOpen &&
        (liabilities.length === 0 ? (
          <p className="px-4 py-3 text-sm text-text-subtle border-b border-border">{t('common:emptyState.none')}</p>
        ) : (
          liabilities.map(({ account, home }) => (
            <AccountRow
              key={account.id}
              name={account.name}
              currency={homeCurrency}
              amount={home}
              colorClass="text-text"
              onOpen={() => navigate(`/accounts/${account.id}`)}
            />
          ))
        ))}
    </Screen>
  );
}

function SummaryRow({
  label,
  amount,
  currency,
  emphasized = false,
  colorClass,
}: {
  label: string;
  amount: number;
  currency: string;
  emphasized?: boolean;
  colorClass?: string;
}) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className={`${emphasized ? 'text-md font-semibold text-text' : 'text-sm text-text-muted'}`}>
        {label}
      </span>
      <span
        className={`font-mono tabular-nums text-right ${
          emphasized ? 'text-md font-semibold' : 'text-sm'
        } ${colorClass ?? 'text-text'}`}
      >
        {formatAmount(amount, currency)}
      </span>
    </div>
  );
}

// ─── Income statement ───────────────────────────────────────────────────────

type Period = 'month' | 'year' | 'all';

const PERIODS: Period[] = ['month', 'year', 'all'];

const PERIOD_LABEL_KEYS = {
  month: 'controls.thisMonth',
  year: 'controls.thisYear',
  all: 'controls.allTime',
} as const;

// Resolve a period to an inclusive [from, to] ISO-date range. 'all' uses a wide
// lower bound so the same date-range filter (matching IncomeStatement.tsx) works.
function periodRange(period: Period): { from: string; to: string } {
  const now = new Date();
  const to = now.toISOString().slice(0, 10);
  if (period === 'year') return { from: `${now.getFullYear()}-01-01`, to };
  if (period === 'month') {
    const from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    return { from, to };
  }
  return { from: '0000-01-01', to };
}

export function MobileIncomeStatement() {
  const { t } = useTranslation(['reports', 'common']);
  const { state } = useApp();
  const { ledger } = state;
  const navigate = useNavigate();
  const homeCurrency = ledger.settings.homeCurrency;
  const periodLabel = (p: Period) => t(PERIOD_LABEL_KEYS[p]);

  const [period, setPeriod] = useState<Period>('year');
  const [periodOpen, setPeriodOpen] = useState(false);
  const [incomeOpen, setIncomeOpen] = useState(true);
  const [expensesOpen, setExpensesOpen] = useState(true);

  const { from: dateFrom, to: dateTo } = periodRange(period);
  const allPrices = useMemo(() => [...ledger.prices.values()], [ledger.prices]);

  const { incomeRows, expenseRows, totalIncome, totalExpenses } = useMemo(() => {
    const incomeMap = new Map<string, number>();
    const expenseMap = new Map<string, number>();

    for (const tx of ledger.transactions.values()) {
      if (tx.voided) continue;
      if (tx.date < dateFrom || tx.date > dateTo) continue;
      for (const split of tx.splits) {
        const account = ledger.accounts.get(split.accountId);
        if (!account) continue;
        const cur = account.currency;
        let homeAmount = split.amount;
        if (cur !== homeCurrency) {
          const rate = getExchangeRate(cur, homeCurrency, tx.date, allPrices);
          if (rate !== null) {
            homeAmount = convertCurrency(
              split.amount,
              cur,
              homeCurrency,
              rate,
              getCurrencyDecimalPlaces(cur),
              getCurrencyDecimalPlaces(homeCurrency),
            );
          }
        }
        if (INCOME_TYPES.has(account.type)) {
          incomeMap.set(account.id, (incomeMap.get(account.id) ?? 0) + homeAmount);
        } else if (EXPENSE_TYPES.has(account.type)) {
          expenseMap.set(account.id, (expenseMap.get(account.id) ?? 0) + homeAmount);
        }
      }
    }

    const toRows = (map: Map<string, number>) =>
      [...map.entries()]
        .map(([id, amount]) => ({ id, name: ledger.accounts.get(id)?.name ?? id, amount }))
        .sort((a, b) => a.name.localeCompare(b.name));

    const incomeRows = toRows(incomeMap);
    const expenseRows = toRows(expenseMap);

    return {
      incomeRows,
      expenseRows,
      totalIncome: incomeRows.reduce((sum, r) => sum + Math.abs(r.amount), 0),
      totalExpenses: expenseRows.reduce((sum, r) => sum + r.amount, 0),
    };
  }, [ledger.transactions, ledger.accounts, dateFrom, dateTo, allPrices, homeCurrency]);

  const netIncome = totalIncome - totalExpenses;

  return (
    <Screen
      title={t('titles.incomeStatement')}
      right={
        <button
          onClick={() => setPeriodOpen(true)}
          className="px-2 h-9 flex items-center text-sm text-accent active:opacity-70"
        >
          {periodLabel(period)}
        </button>
      }
    >
      <div className="px-4 py-4 border-b border-border bg-bg-surface">
        <p className="text-xs text-text-subtle mb-2">
          {period === 'all' ? t('controls.allTime') : t('dateRange', { from: dateFrom, to: dateTo })}
        </p>
        <SummaryRow
          label={t('sections.income')}
          amount={totalIncome}
          currency={homeCurrency}
          colorClass="text-positive"
        />
        <SummaryRow
          label={t('sections.expenses')}
          amount={totalExpenses}
          currency={homeCurrency}
          colorClass="text-negative"
        />
        <div className="mt-1 pt-2 border-t border-border">
          <SummaryRow
            label={t('sections.netIncome')}
            amount={netIncome}
            currency={homeCurrency}
            emphasized
            colorClass={netIncome >= 0 ? 'text-positive' : 'text-negative'}
          />
        </div>
      </div>

      <SectionHeader label={t('sections.income')} open={incomeOpen} onToggle={() => setIncomeOpen((x) => !x)} />
      {incomeOpen &&
        (incomeRows.length === 0 ? (
          <p className="px-4 py-3 text-sm text-text-subtle border-b border-border">{t('empty.noIncome')}</p>
        ) : (
          incomeRows.map((r) => (
            <AccountRow
              key={r.id}
              name={r.name}
              currency={homeCurrency}
              amount={Math.abs(r.amount)}
              colorClass="text-positive"
              onOpen={() => navigate(`/accounts/${r.id}`)}
            />
          ))
        ))}

      <SectionHeader
        label={t('sections.expenses')}
        open={expensesOpen}
        onToggle={() => setExpensesOpen((x) => !x)}
      />
      {expensesOpen &&
        (expenseRows.length === 0 ? (
          <p className="px-4 py-3 text-sm text-text-subtle border-b border-border">{t('empty.noExpenses')}</p>
        ) : (
          expenseRows.map((r) => (
            <AccountRow
              key={r.id}
              name={r.name}
              currency={homeCurrency}
              amount={r.amount}
              colorClass="text-negative"
              onOpen={() => navigate(`/accounts/${r.id}`)}
            />
          ))
        ))}

      <BottomSheet open={periodOpen} onClose={() => setPeriodOpen(false)} title={t('controls.period')}>
        {PERIODS.map((p) => (
          <button
            key={p}
            onClick={() => {
              setPeriod(p);
              setPeriodOpen(false);
            }}
            className="w-full touch-row flex items-center justify-between px-4 h-12 border-b border-border text-left active:bg-bg-elevated"
          >
            <span className="text-md text-text">{periodLabel(p)}</span>
            {period === p && <span className="text-accent text-sm">{t('controls.selected')}</span>}
          </button>
        ))}
      </BottomSheet>
    </Screen>
  );
}

// ─── Transaction report ─────────────────────────────────────────────────────

interface TxRow {
  key: string;
  date: string;
  description: string;
  accountName: string;
  amount: number;
  currency: string;
}

function formatShortDate(dateStr: string): string {
  return formatDate(dateStr, { month: '2-digit', day: '2-digit', year: '2-digit' });
}

export function MobileTransactionReport() {
  const { t } = useTranslation(['reports', 'mobile', 'common']);
  const { state } = useApp();
  const { ledger } = state;

  const now = new Date();
  const [dateFrom, setDateFrom] = useState(`${now.getFullYear()}-01-01`);
  const [dateTo, setDateTo] = useState(now.toISOString().slice(0, 10));
  const [text, setText] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // Mirrors TransactionReport.tsx's row construction (one row per split), then
  // adds a free-text filter over description + account name for mobile search.
  const rows = useMemo(() => {
    const out: TxRow[] = [];
    const q = text.trim().toLowerCase();

    for (const tx of ledger.transactions.values()) {
      if (tx.voided) continue;
      if (tx.date < dateFrom || tx.date > dateTo) continue;

      for (const split of tx.splits) {
        const account = ledger.accounts.get(split.accountId);
        if (!account) continue;

        if (
          q &&
          !tx.description.toLowerCase().includes(q) &&
          !account.name.toLowerCase().includes(q)
        ) {
          continue;
        }

        out.push({
          key: `${tx.id}:${split.splitId}`,
          date: tx.date,
          description: tx.description,
          accountName: account.name,
          amount: split.amount,
          currency: tx.currency,
        });
      }
    }

    out.sort((a, b) => b.date.localeCompare(a.date) || a.description.localeCompare(b.description));
    return out;
  }, [ledger.transactions, ledger.accounts, dateFrom, dateTo, text]);

  const total = rows.reduce((sum, r) => sum + r.amount, 0);

  return (
    <Screen
      title={t('titles.transactions')}
      right={
        <button
          onClick={() => setFilterOpen(true)}
          aria-label={t('mobile:reports.filterTransactions')}
          className="w-10 h-10 flex items-center justify-center text-text-muted active:text-text"
        >
          <SearchIcon size={20} />
        </button>
      }
    >
      <div className="px-4 py-2 border-b border-border bg-bg-surface flex items-center justify-between">
        <span className="text-xs text-text-subtle">
          {t('rowCount', { count: rows.length })}
        </span>
        <span className={`text-sm font-mono tabular-nums ${total >= 0 ? 'text-positive' : 'text-negative'}`}>
          {formatAmount(total)}
        </span>
      </div>

      {rows.length === 0 ? (
        <p className="text-center text-text-muted text-sm py-16">{t('empty.noTransactionsFound')}</p>
      ) : (
        rows.map((r) => (
          <div key={r.key} className="px-4 py-2.5 border-b border-border">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-md text-text truncate">{r.description || t('mobile:noDescription')}</p>
                <p className="text-xs text-text-subtle truncate">{r.accountName}</p>
              </div>
              <div className="text-right shrink-0">
                <p
                  className={`text-sm font-mono tabular-nums ${
                    r.amount >= 0 ? 'text-positive' : 'text-negative'
                  }`}
                >
                  {formatAmount(r.amount, r.currency)}
                </p>
                <p className="text-xs text-text-subtle font-mono">{formatShortDate(r.date)}</p>
              </div>
            </div>
          </div>
        ))
      )}

      <BottomSheet open={filterOpen} onClose={() => setFilterOpen(false)} title={t('controls.filter')}>
        <div className="px-4 py-3 space-y-3">
          <div>
            <label className="block text-xs text-text-muted mb-1">{t('common:nav.search')}</label>
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t('mobile:reports.descriptionOrAccount')}
              className="w-full bg-bg border border-border text-text text-md px-3 h-11 focus:border-accent focus:outline-none placeholder-text-subtle"
            />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs text-text-muted mb-1">{t('controls.from')}</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full bg-bg border border-border text-text text-md px-3 h-11 focus:border-accent focus:outline-none"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-text-muted mb-1">{t('controls.to')}</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full bg-bg border border-border text-text text-md px-3 h-11 focus:border-accent focus:outline-none"
              />
            </div>
          </div>
          <button
            onClick={() => setFilterOpen(false)}
            className="w-full h-11 bg-accent text-white font-medium active:opacity-80"
          >
            {t('common:actions.done')}
          </button>
        </div>
      </BottomSheet>
    </Screen>
  );
}

// ─── Chart reports ──────────────────────────────────────────────────────────

// Generic mobile chart shell: top bar + a padded, tab-bar-clearing scroll region.
// The wrapped desktop recharts components (NetWorthChart, ExpenseBreakdown,
// IncomeVsExpenses) are already responsive via <ResponsiveContainer>.
export function MobileChartReport({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Screen title={title}>{children}</Screen>
  );
}

export function MobileNetWorth() {
  const { t } = useTranslation(['reports']);
  return (
    <MobileChartReport title={t('titles.netWorth')}>
      <NetWorthChart />
    </MobileChartReport>
  );
}

export function MobileExpenseBreakdown() {
  const { t } = useTranslation(['reports']);
  return (
    <MobileChartReport title={t('titles.expenseBreakdown')}>
      <ExpenseBreakdown />
    </MobileChartReport>
  );
}

export function MobileIncomeVsExpenses() {
  const { t } = useTranslation(['reports']);
  return (
    <MobileChartReport title={t('titles.incomeVsExpenses')}>
      <IncomeVsExpenses />
    </MobileChartReport>
  );
}
